<template>
  <div class="student-data-report">
    <Manpower_bar />

    <v-container fluid class="pa-6">
      <!-- Header Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="header-section">
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                <h1 class="page-title">
                  <v-icon large color="white" class="mr-3">mdi-school</v-icon>
                  จำนวนนักเรียน จากระบบ ศธ.02
                </h1>
                <p class="page-subtitle mt-2">
                  ระบบจัดการข้อมูลนักเรียน นักศึกษา
                  สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา
                </p>
              </div>
              <div class="d-flex align-center header-badges">
                <v-chip class="mr-3 header-chip" dark>
                  <v-icon left small>mdi-calendar</v-icon>
                  ปีการศึกษา {{ searchYear || getCurrentYear() }}
                </v-chip>
                <v-chip class="header-chip" color="white" outlined>
                  <v-icon left small color="white">mdi-account-group</v-icon>
                  <span class="white--text"
                    >{{ formatNumber(totalStudents) }} คน</span
                  >
                </v-chip>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Statistics Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-blue" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">นักเรียน ปวช.</p>
                  <h2 class="stat-value">
                    {{ formatNumber(statistics.totalVC) }}
                  </h2>
                  <p class="stat-percent">
                    {{ statistics.vcPercent }}% ของทั้งหมด
                  </p>
                </div>
                <v-avatar size="56" color="info" class="stat-icon">
                  <v-icon dark size="28">mdi-account-school</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear
                :value="statistics.vcPercent"
                color="info"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-purple" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">นักศึกษา ปวส.</p>
                  <h2 class="stat-value">
                    {{ formatNumber(statistics.totalHVC) }}
                  </h2>
                  <p class="stat-percent">
                    {{ statistics.hvcPercent }}% ของทั้งหมด
                  </p>
                </div>
                <v-avatar size="56" color="primary" class="stat-icon">
                  <v-icon dark size="28">mdi-school</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear
                :value="statistics.hvcPercent"
                color="primary"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-teal" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">รวมทั้งหมด</p>
                  <h2 class="stat-value">{{ formatNumber(totalStudents) }}</h2>
                  <p class="stat-percent">นักเรียน นักศึกษา</p>
                </div>
                <v-avatar size="56" color="info" class="stat-icon">
                  <v-icon dark size="28">mdi-account-group</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear
                :value="100"
                color="info"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card stat-card-orange" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <p class="stat-label mb-1">จำนวนสาขาวิชา</p>
                  <h2 class="stat-value">
                    {{ formatNumber(statistics.totalCourses) }}
                  </h2>
                  <p class="stat-percent">สาขาวิชา</p>
                </div>
                <v-avatar size="56" color="warning" class="stat-icon">
                  <v-icon dark size="28">mdi-book-open-variant</v-icon>
                </v-avatar>
              </div>
              <v-progress-linear
                :value="100"
                color="warning"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Section -->
      <v-row class="mb-6">
        <v-col cols="12" md="5">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <v-icon color="primary" class="mr-2">mdi-chart-pie</v-icon>
              สัดส่วนนักเรียน ปวช./ปวส.
            </v-card-title>
            <v-card-text>
              <apexchart
                ref="pieChart"
                type="pie"
                height="320"
                :options="pieChartOptions"
                :series="pieSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card class="chart-card" elevation="4">
            <v-card-title class="chart-title">
              <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
              จำนวนนักเรียนตามระดับชั้น
            </v-card-title>
            <v-card-text>
              <apexchart
                ref="barChart"
                type="bar"
                height="320"
                :options="barChartOptions"
                :series="barSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content -->
      <v-row>
        <!-- Control Panel -->
        <v-col cols="12" md="3">
          <v-card class="control-panel" elevation="4">
            <v-card-title class="control-title">
              <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
              ตัวกรองข้อมูล
            </v-card-title>
            <v-card-text>
              <!-- ── ปีการศึกษา ── -->
              <v-select
                v-model="searchYear"
                :items="years"
                label="เลือกปีการศึกษา"
                prepend-inner-icon="mdi-calendar"
                outlined
                dense
                class="mb-4"
                @change="onYearChange()"
              >
                <template v-slot:item="{ item }"
                  ><span>พ.ศ. {{ item }}</span></template
                >
                <template v-slot:selection="{ item }"
                  ><span>พ.ศ. {{ item }}</span></template
                >
              </v-select>

              <!-- ── วิทยาลัย ── -->
              <v-autocomplete
                v-model="collegeSearch"
                :items="colleges"
                item-text="college_name"
                item-value="college_code"
                label="ค้นหาวิทยาลัย"
                prepend-inner-icon="mdi-domain"
                outlined
                dense
                clearable
                class="mb-4"
                @change="applyFilters()"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{
                      item.college_name
                    }}</v-list-item-title>
                    <v-list-item-subtitle
                      >รหัส: {{ item.college_code }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <v-divider class="mb-3"></v-divider>

              <!-- ══ กรองขั้นสูง header ══ -->
              <div
                class="d-flex align-center justify-space-between mb-2 advanced-filter-header"
                @click="showAdvanced = !showAdvanced"
                style="cursor:pointer"
              >
                <span class="action-label">
                  <v-icon small class="mr-1">mdi-tune</v-icon>
                  กรองขั้นสูง
                </span>
                <div class="d-flex align-center">
                  <!-- badge แสดงจำนวน filter ที่ active -->
                  <v-chip
                    v-if="activeFilterCount > 0"
                    x-small
                    color="primary"
                    dark
                    class="mr-2"
                  >
                    {{ activeFilterCount }}
                  </v-chip>
                  <v-icon small>{{
                    showAdvanced ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                </div>
              </div>

              <v-expand-transition>
                <div v-show="showAdvanced">
                  <!-- ── ระดับชั้น (ปวช./ปวส.) ── -->
                  <div class="filter-section mb-3">
                    <p class="filter-label">ระดับชั้น</p>
                    <v-autocomplete
                      v-model="filterLevels"
                      :items="levelOptions"
                      item-text="text"
                      item-value="value"
                      label="เลือกระดับชั้น"
                      prepend-inner-icon="mdi-layers"
                      outlined
                      dense
                      multiple
                      clearable
                      class="multi-select-field"
                      @change="applyFilters()"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 2"
                          small
                          :color="item.color"
                          dark
                          class="mr-1 mb-1"
                        >
                          {{ item.text }}
                        </v-chip>
                        <span v-if="index === 2" class="caption grey--text"
                          >+{{ filterLevels.length - 2 }}</span
                        >
                      </template>
                      <template v-slot:item="{ item, attrs, on }">
                        <v-list-item v-bind="attrs" v-on="on" dense>
                          <v-list-item-action class="mr-2">
                            <v-checkbox
                              :input-value="attrs.inputValue"
                              :color="item.color"
                              dense
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>
                              <v-chip
                                x-small
                                :color="item.color"
                                dark
                                class="mr-2"
                                >{{ item.text }}</v-chip
                              >
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </div>

                  <!-- ── ประเภทวิชา ── -->
                  <div class="filter-section mb-3">
                    <p class="filter-label">ประเภทวิชา</p>
                    <v-autocomplete
                      v-model="filterSubjectTypes"
                      :items="subjectTypeOptions"
                      item-text="subjectTypeNameTh"
                      item-value="subjectTypeID"
                      label="เลือกประเภทวิชา"
                      prepend-inner-icon="mdi-book-education"
                      outlined
                      dense
                      multiple
                      clearable
                      class="multi-select-field"
                      :loading="loadingCurriculum"
                      @change="onSubjectTypeChange()"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 1"
                          small
                          color="primary"
                          dark
                          class="mr-1 mb-1"
                        >
                          <span class="text-truncate" style="max-width:90px">{{
                            item.subjectTypeNameTh
                          }}</span>
                        </v-chip>
                        <span v-if="index === 1" class="caption grey--text"
                          >+{{ filterSubjectTypes.length - 1 }}</span
                        >
                      </template>
                      <template v-slot:item="{ item, attrs, on }">
                        <v-list-item v-bind="attrs" v-on="on" dense>
                          <v-list-item-action class="mr-2">
                            <v-checkbox
                              :input-value="attrs.inputValue"
                              color="primary"
                              dense
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title class="body-2">{{
                              item.subjectTypeNameTh
                            }}</v-list-item-title>
                            <v-list-item-subtitle class="caption">{{
                              item.subjectTypeNameEn
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </div>

                  <!-- ── สาขาวิชา (major) ── -->
                  <div class="filter-section mb-3">
                    <p class="filter-label">สาขาวิชา</p>
                    <v-autocomplete
                      v-model="filterMajors"
                      :items="majorOptions"
                      item-text="majorNameTh"
                      item-value="majorID"
                      label="เลือกสาขาวิชา"
                      prepend-inner-icon="mdi-certificate"
                      outlined
                      dense
                      multiple
                      clearable
                      class="multi-select-field"
                      :loading="loadingCurriculum"
                      :disabled="majorOptions.length === 0"
                      :hint="
                        majorOptions.length === 0 ? 'เลือกประเภทวิชาก่อน' : ''
                      "
                      persistent-hint
                      @change="applyFilters()"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 1"
                          small
                          color="info"
                          dark
                          class="mr-1 mb-1"
                        >
                          <span class="text-truncate" style="max-width:90px">{{
                            item.majorNameTh
                          }}</span>
                        </v-chip>
                        <span v-if="index === 1" class="caption grey--text"
                          >+{{ filterMajors.length - 1 }}</span
                        >
                      </template>
                      <template v-slot:item="{ item, attrs, on }">
                        <v-list-item v-bind="attrs" v-on="on" dense>
                          <v-list-item-action class="mr-2">
                            <v-checkbox
                              :input-value="attrs.inputValue"
                              color="info"
                              dense
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title class="body-2">{{
                              item.majorNameTh
                            }}</v-list-item-title>
                            <v-list-item-subtitle class="caption">{{
                              item.majorNameEn
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </div>

                  <!-- ── ล้างตัวกรองขั้นสูง ── -->
                  <v-btn
                    v-if="activeFilterCount > 0"
                    block
                    small
                    text
                    color="error"
                    class="mb-3"
                    @click="clearAdvancedFilters()"
                  >
                    <v-icon left small>mdi-filter-remove</v-icon>
                    ล้างตัวกรองขั้นสูง ({{ activeFilterCount }})
                  </v-btn>
                </div>
              </v-expand-transition>

              <v-divider class="mb-4"></v-divider>

              <!-- นำเข้าข้อมูล -->
              <p class="action-label mb-3">
                <v-icon small class="mr-1">mdi-database-import</v-icon>
                นำเข้าข้อมูล
              </p>
              <v-btn
                block
                color="primary"
                class="mb-3 action-btn"
                @click="rate_work_collegeCSV()"
              >
                <v-icon left>mdi-file-delimited</v-icon>
                นำเข้าไฟล์ CSV
              </v-btn>
              <v-btn
                block
                color="primary"
                dark
                class="mb-4 action-btn"
                @click="std_getstudentcountQuery()"
              >
                <v-icon left>mdi-cloud-download</v-icon>
                นำเข้าจาก API ศธ.02
              </v-btn>

              <v-divider class="mb-4"></v-divider>

              <!-- Summary Card -->
              <v-card outlined class="summary-card pa-3">
                <p class="summary-title mb-2">
                  <v-icon small color="primary" class="mr-1"
                    >mdi-chart-box</v-icon
                  >
                  สรุปข้อมูลตามระดับชั้น
                </p>
                <v-simple-table dense class="summary-table">
                  <tbody>
                    <tr>
                      <td class="text-left font-weight-medium">ปวช.1</td>
                      <td class="text-right">
                        {{ formatNumber(statistics.ch1) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-left font-weight-medium">ปวช.2</td>
                      <td class="text-right">
                        {{ formatNumber(statistics.ch2) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-left font-weight-medium">ปวช.3</td>
                      <td class="text-right">
                        {{ formatNumber(statistics.ch3) }}
                      </td>
                    </tr>
                    <tr class="summary-divider-row">
                      <td class="text-left font-weight-medium">ปวส.1</td>
                      <td class="text-right">
                        {{ formatNumber(statistics.s1) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-left font-weight-medium">ปวส.2</td>
                      <td class="text-right">
                        {{ formatNumber(statistics.s2) }}
                      </td>
                    </tr>
                    <tr class="summary-total-row">
                      <td class="text-left font-weight-bold primary--text">
                        รวม
                      </td>
                      <td class="text-right font-weight-bold primary--text">
                        {{ formatNumber(totalStudents) }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Data Table -->
        <v-col cols="12" md="9">
          <v-card class="data-table-card" elevation="4">
            <v-card-title
              class="table-title d-flex justify-space-between flex-wrap"
            >
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                <span>รายละเอียดข้อมูลนักเรียน นักศึกษา</span>
                <!-- แสดง active filter chips -->
                <template v-if="activeFilterCount > 0">
                  <v-chip
                    v-for="lv in filterLevels"
                    :key="'lv-' + lv"
                    x-small
                    close
                    class="ml-2"
                    :color="lv === '20' ? 'info' : 'primary'"
                    dark
                    @click:close="removeFilterLevel(lv)"
                    >{{ lv === "20" ? "ปวช." : "ปวส." }}</v-chip
                  >
                  <v-chip
                    v-for="st in filterSubjectTypes"
                    :key="'st-' + st"
                    x-small
                    close
                    class="ml-2"
                    color="primary"
                    dark
                    @click:close="removeFilterSubjectType(st)"
                    >{{ getSubjectTypeName(st) }}</v-chip
                  >
                  <v-chip
                    v-for="mj in filterMajors"
                    :key="'mj-' + mj"
                    x-small
                    close
                    class="ml-2"
                    color="info"
                    dark
                    @click:close="removeFilterMajor(mj)"
                    >{{ getMajorName(mj) }}</v-chip
                  >
                </template>
              </div>
              <div class="d-flex align-center table-actions">
                <v-btn
                  color="success"
                  class="mr-2"
                  @click="exportToExcel()"
                  :disabled="!filteredData.length"
                >
                  <v-icon left>mdi-microsoft-excel</v-icon>
                  ส่งออก Excel
                </v-btn>
              </div>
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหาข้อมูล..."
                outlined
                dense
                clearable
                hide-details
                class="mb-4 search-field"
              ></v-text-field>

              <v-data-table
                :loading="loading"
                :headers="headers"
                :items="filteredData"
                :search="search"
                :items-per-page="15"
                :footer-props="{
                  'items-per-page-options': [10, 15, 25, 50, -1],
                  'items-per-page-text': 'แสดงรายการ:'
                }"
                class="modern-table"
                loading-text="กำลังโหลดข้อมูล..."
                no-data-text="ไม่พบข้อมูล กรุณาเลือกปีการศึกษา"
                no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
              >
                <template v-slot:item.rate_work_college_year="{ item }">
                  <v-chip small color="primary" dark>{{
                    item.rate_work_college_year
                  }}</v-chip>
                </template>
                <template v-slot:item.college_name="{ item }">
                  <div class="college-name-cell">
                    <strong>{{ item.college_name }}</strong>
                  </div>
                </template>
                <template v-slot:item.rate_work_college_id_class="{ item }">
                  <v-chip
                    small
                    :color="
                      String(item.rate_work_college_id_class) === '20'
                        ? 'info'
                        : 'primary'
                    "
                    dark
                  >
                    {{
                      String(item.rate_work_college_id_class) === "20"
                        ? "ปวช."
                        : "ปวส."
                    }}
                  </v-chip>
                </template>
                <template v-slot:item.subject_typeName="{ item }">
                  <span class="subject-type">{{ item.subject_typeName }}</span>
                </template>
                <template v-slot:item.name_type_course="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="course-name">
                        {{ truncateText(item.name_type_course, 30) }}
                      </span>
                    </template>
                    <span>{{ item.name_type_course }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.rate_work_field_study_lavel_1="{ item }">
                  <span class="number-cell">{{
                    formatNumber(item.rate_work_field_study_lavel_1)
                  }}</span>
                </template>
                <template v-slot:item.rate_work_field_study_lavel_2="{ item }">
                  <span class="number-cell">{{
                    formatNumber(item.rate_work_field_study_lavel_2)
                  }}</span>
                </template>
                <template v-slot:item.rate_work_field_study_lavel_3="{ item }">
                  <span class="number-cell">{{
                    formatNumber(item.rate_work_field_study_lavel_3)
                  }}</span>
                </template>
                <template v-slot:item.total_row="{ item }">
                  <span class="number-cell font-weight-bold">{{
                    formatNumber(item.total_row)
                  }}</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- CSV Import Dialog -->
      <v-dialog v-model="csvrate_work_collegedialog" max-width="600" persistent>
        <v-card class="import-dialog">
          <v-card-title class="primary white--text">
            <v-icon left dark>mdi-file-delimited</v-icon>
            นำเข้าข้อมูลจากไฟล์ CSV
          </v-card-title>
          <v-card-text class="pt-6">
            <v-alert type="info" outlined dense class="mb-4">
              กรุณาเลือกไฟล์ CSV ที่มีรูปแบบตามที่กำหนด
            </v-alert>
            <v-file-input
              ref="fileInput"
              v-model="selectedFile"
              label="เลือกไฟล์ CSV"
              accept=".csv"
              prepend-icon="mdi-file-upload"
              outlined
              show-size
              counter
              :rules="fileRules"
            >
              <template v-slot:selection="{ text }">
                <v-chip small color="primary" dark>{{ text }}</v-chip>
              </template>
            </v-file-input>
            <v-btn
              color="info"
              text
              to="/admin/rate_work_college_import/"
              target="_blank"
              class="mt-2"
            >
              <v-icon left>mdi-download</v-icon> ดาวน์โหลดไฟล์ตัวอย่าง
            </v-btn>
          </v-card-text>
          <v-card-actions class="pa-4 grey lighten-4">
            <v-spacer></v-spacer>
            <v-btn text @click="csvrate_work_collegedialog = false"
              ><v-icon left>mdi-close</v-icon>ยกเลิก</v-btn
            >
            <v-btn
              color="success"
              @click="csvrate_work_collegeSubmit()"
              :loading="loading"
              :disabled="!selectedFile"
            >
              <v-icon left>mdi-upload</v-icon> นำเข้าข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- API Import Dialog -->
      <v-dialog v-model="stdrate_work_collegedialog" max-width="500" persistent>
        <v-card class="import-dialog">
          <v-card-title class="deep-purple white--text">
            <v-icon left dark>mdi-cloud-download</v-icon>
            นำเข้าข้อมูลจาก API ศธ.02
          </v-card-title>
          <v-card-text class="pt-6">
            <v-alert type="info" outlined dense class="mb-4">
              ระบบจะดึงข้อมูลนักเรียนจากระบบ ศธ.02 โดยอัตโนมัติ
            </v-alert>
            <v-text-field
              v-model="AcademicYearst"
              label="ปีการศึกษา"
              type="number"
              prepend-inner-icon="mdi-calendar"
              outlined
              dense
              class="mb-4"
              :rules="[v => !!v || 'กรุณาระบุปีการศึกษา']"
            ></v-text-field>
            <v-select
              v-model="Semesterst"
              :items="Semester"
              item-text="text"
              item-value="value"
              label="ข้อมูล ณ วันที่"
              prepend-inner-icon="mdi-calendar-range"
              outlined
              dense
              :rules="[v => !!v || 'กรุณาเลือกวันที่']"
            ></v-select>
          </v-card-text>
          <v-card-actions class="pa-4 grey lighten-4">
            <v-spacer></v-spacer>
            <v-btn text @click="stdrate_work_collegedialog = false"
              ><v-icon left>mdi-close</v-icon>ยกเลิก</v-btn
            >
            <v-btn
              color="primary"
              dark
              @click="stdrate_work_collegeSubmit()"
              :loading="loading"
              :disabled="!AcademicYearst || !Semesterst"
            >
              <v-icon left>mdi-cloud-sync</v-icon> ดึงข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
          {{ snackbar.text }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import Manpower_bar from "../../../components/admin/manpower_bar.vue";
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";

export default {
  name: "StudentDataReport",
  components: { Manpower_bar, apexchart: VueApexCharts },

  data() {
    const currentYear = new Date().getFullYear() + 543;
    return {
      loading: false,
      loadingCurriculum: false,
      ApiKey: "HRvec2021",

      // Dialogs
      csvrate_work_collegedialog: false,
      stdrate_work_collegedialog: false,

      // Snackbar
      snackbar: { show: false, color: "", timeout: 5000, icon: "", text: "" },

      // Raw data from API
      rate_work_colleges: [],
      rate_work_collegesReport: [],
      colleges: [],

      // Curriculum data (from curriculum table)
      curriculumList: [],

      // Search & filters
      search: "",
      searchYear: currentYear,
      collegeSearch: "",
      selectedFile: null,

      // ── Advanced filters ──
      showAdvanced: false,
      filterLevels: [], // ['20', '30']
      filterSubjectTypes: [], // subjectTypeID[]
      filterMajors: [], // majorID[]

      levelOptions: [
        { text: "ปวช.", value: "20", color: "blue" },
        { text: "ปวส.", value: "30", color: "primary" }
      ],

      // API Import
      AcademicYearst: currentYear,
      Semesterst: "",
      Semester: [
        { text: "10 มิถุนายน", value: "1" },
        { text: "10 พฤศจิกายน", value: "2" }
      ],
      years: [],
      fileRules: [
        v => !!v || "กรุณาเลือกไฟล์",
        v => (v && v.size < 5000000) || "ขนาดไฟล์ต้องน้อยกว่า 5 MB"
      ],

      // Table headers (เพิ่ม คอลัมน์ รวม)
      headers: [
        {
          text: "ปีการศึกษา",
          align: "center",
          value: "rate_work_college_year",
          width: "100"
        },
        {
          text: "วิทยาลัย",
          align: "left",
          value: "college_name",
          width: "200"
        },
        {
          text: "ประเภทวิชา",
          align: "center",
          value: "subject_typeName",
          width: "120"
        },
        {
          text: "ระดับชั้น",
          align: "center",
          value: "rate_work_college_id_class",
          width: "90"
        },
        {
          text: "สาขาวิชา",
          align: "left",
          value: "name_type_course",
          width: "200"
        },
        {
          text: "ชั้นปีที่ 1",
          align: "right",
          value: "rate_work_field_study_lavel_1",
          width: "90"
        },
        {
          text: "ชั้นปีที่ 2",
          align: "right",
          value: "rate_work_field_study_lavel_2",
          width: "90"
        },
        {
          text: "ชั้นปีที่ 3",
          align: "right",
          value: "rate_work_field_study_lavel_3",
          width: "90"
        },
        { text: "รวม", align: "right", value: "total_row", width: "90" }
      ],

      // Chart options
      pieChartOptions: {
        chart: {
          type: "pie",
          fontFamily: "Sarabun, sans-serif",
          animations: { enabled: true, easing: "easeinout", speed: 800 }
        },
        labels: ["ปวช.1", "ปวช.2", "ปวช.3", "ปวส.1", "ปวส.2"],
        colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014"],
        legend: {
          position: "bottom",
          fontSize: "14px",
          fontFamily: "Sarabun, sans-serif"
        },
        dataLabels: { enabled: true, formatter: val => val.toFixed(1) + "%" },
        tooltip: { y: { formatter: val => val.toLocaleString() + " คน" } },
        responsive: [
          {
            breakpoint: 480,
            options: { chart: { width: 280 }, legend: { position: "bottom" } }
          }
        ]
      },
      barChartOptions: {
        chart: {
          type: "bar",
          fontFamily: "Sarabun, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          animations: { enabled: true, easing: "easeinout", speed: 800 }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            columnWidth: "55%",
            distributed: true
          }
        },
        xaxis: {
          categories: ["ปวช.1", "ปวช.2", "ปวช.3", "ปวส.1", "ปวส.2"],
          labels: {
            style: { fontSize: "12px", fontFamily: "Sarabun, sans-serif" }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: { fontSize: "12px", fontFamily: "Sarabun, sans-serif" }
          },
          labels: { formatter: val => val.toLocaleString() }
        },
        colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014"],
        legend: { show: false },
        fill: { opacity: 1 },
        dataLabels: {
          enabled: true,
          formatter: val => val.toLocaleString(),
          style: { fontSize: "11px", fontFamily: "Sarabun, sans-serif" }
        },
        tooltip: { y: { formatter: val => val.toLocaleString() + " คน" } }
      }
    };
  },

  computed: {
    // ── จำนวน active advanced filters ──
    activeFilterCount() {
      return (
        this.filterLevels.length +
        this.filterSubjectTypes.length +
        this.filterMajors.length
      );
    },

    // ── ตัวเลือกประเภทวิชา (unique จาก curriculumList) ──
    // degreeLevelID "10"=ปวช.(id_class 20), "30"=ปวส.(id_class 30)
    subjectTypeOptions() {
      const seen = new Set();
      const result = [];
      for (const c of this.curriculumList) {
        if (this.filterLevels.length > 0) {
          // แปลง degreeLevelID → id_class inline (ไม่ใช้ computed method)
          const idClass = String(c.degreeLevelID) === "10" ? "20" : "30";
          if (!this.filterLevels.includes(idClass)) continue;
        }
        const key = String(c.subjectTypeID);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({
          subjectTypeID: key,
          subjectTypeNameTh: c.subjectTypeNameTh || "",
          subjectTypeNameEn: c.subjectTypeNameEn || ""
        });
      }
      return result.sort((a, b) =>
        (a.subjectTypeNameTh || "").localeCompare(
          b.subjectTypeNameTh || "",
          "th"
        )
      );
    },

    // ── ตัวเลือกสาขาวิชา (กรองตาม subjectType ที่เลือก) ──
    majorOptions() {
      if (this.filterSubjectTypes.length === 0) return [];
      const seen = new Set();
      const result = [];
      for (const c of this.curriculumList) {
        if (!this.filterSubjectTypes.includes(String(c.subjectTypeID)))
          continue;
        if (this.filterLevels.length > 0) {
          const idClass = String(c.degreeLevelID) === "10" ? "20" : "30";
          if (!this.filterLevels.includes(idClass)) continue;
        }
        const key = String(c.majorID);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({
          majorID: key,
          majorNameTh: c.majorNameTh || "",
          majorNameEn: c.majorNameEn || ""
        });
      }
      return result.sort((a, b) =>
        (a.majorNameTh || "").localeCompare(b.majorNameTh || "", "th")
      );
    },

    // ── ข้อมูลที่ผ่านการ filter ขั้นสูงแล้ว (ใช้กับ table + stats + excel) ──
    filteredData() {
      let data = this.rate_work_colleges;

      // filter ระดับชั้น
      if (this.filterLevels.length > 0) {
        data = data.filter(item =>
          this.filterLevels.includes(String(item.rate_work_college_id_class))
        );
      }

      // filter ประเภทวิชา — เทียบชื่อ subject_typeName กับที่เลือก
      if (this.filterSubjectTypes.length > 0) {
        const selectedNames = new Set(
          this.filterSubjectTypes
            .map(id => this.getSubjectTypeName(id))
            .filter(Boolean)
        );
        data = data.filter(item => selectedNames.has(item.subject_typeName));
      }

      // filter สาขาวิชา — เทียบชื่อ name_type_course กับที่เลือก
      if (this.filterMajors.length > 0) {
        const selectedMajorNames = new Set(
          this.filterMajors.map(id => this.getMajorName(id)).filter(Boolean)
        );
        data = data.filter(item =>
          selectedMajorNames.has(item.name_type_course)
        );
      }

      // เพิ่ม field total_row ให้แต่ละแถว
      return data.map(item => {
        const lv1 = parseInt(item.rate_work_field_study_lavel_1) || 0;
        const lv2 = parseInt(item.rate_work_field_study_lavel_2) || 0;
        const lv3 = parseInt(item.rate_work_field_study_lavel_3) || 0;
        const isVC = String(item.rate_work_college_id_class) === "20";
        return { ...item, total_row: isVC ? lv1 + lv2 + lv3 : lv1 + lv2 };
      });
    },

    // ── สถิติคำนวณจาก filteredData ──
    statistics() {
      let ch1 = 0,
        ch2 = 0,
        ch3 = 0,
        s1 = 0,
        s2 = 0;
      this.filteredData.forEach(item => {
        const lv1 = parseInt(item.rate_work_field_study_lavel_1) || 0;
        const lv2 = parseInt(item.rate_work_field_study_lavel_2) || 0;
        const lv3 = parseInt(item.rate_work_field_study_lavel_3) || 0;
        if (String(item.rate_work_college_id_class) === "20") {
          ch1 += lv1;
          ch2 += lv2;
          ch3 += lv3;
        } else if (String(item.rate_work_college_id_class) === "30") {
          s1 += lv1;
          s2 += lv2;
        }
      });
      const totalVC = ch1 + ch2 + ch3;
      const totalHVC = s1 + s2;
      const total = totalVC + totalHVC;
      const uniqueCourses = new Set(
        this.filteredData.map(item => item.name_type_course)
      );
      return {
        totalVC,
        totalHVC,
        total,
        totalCourses: uniqueCourses.size,
        vcPercent: total > 0 ? ((totalVC / total) * 100).toFixed(1) : 0,
        hvcPercent: total > 0 ? ((totalHVC / total) * 100).toFixed(1) : 0,
        ch1,
        ch2,
        ch3,
        s1,
        s2
      };
    },

    totalStudents() {
      return this.statistics.total;
    },
    pieSeries() {
      const { ch1, ch2, ch3, s1, s2 } = this.statistics;
      return [ch1, ch2, ch3, s1, s2];
    },
    barSeries() {
      const { ch1, ch2, ch3, s1, s2 } = this.statistics;
      return [{ name: "จำนวนนักเรียน", data: [ch1, ch2, ch3, s1, s2] }];
    }
  },

  async mounted() {
    this.years = this.generateYearOptions();
    await Promise.all([
      this.loadColleges(),
      this.loadCurriculum(),
      this.searchYearSubmit()
    ]);
  },

  methods: {
    generateYearOptions() {
      const y = new Date().getFullYear() + 543;
      const arr = [];
      for (let i = y - 3; i <= y + 3; i++) arr.push(i);
      return arr;
    },
    getCurrentYear() {
      return new Date().getFullYear() + 543;
    },
    formatNumber(num) {
      if (num === null || num === undefined || num === "") return "0";
      return Number(num).toLocaleString("th-TH");
    },
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    showSnackbar(color, icon, text) {
      this.snackbar = { show: true, color, icon, text, timeout: 5000 };
    },
    async sweetAlertLoading() {
      Swal.fire({
        title: "กำลังดำเนินการ...",
        html: "กรุณารอสักครู่",
        timerProgressBar: true,
        didOpen: () => Swal.showLoading(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false
      });
    },

    // ── Helper: ชื่อจาก ID (ค้น curriculumList โดยตรง, String() ครอบทุกการเปรียบเทียบ) ──
    getSubjectTypeName(id) {
      const found = this.curriculumList.find(
        c => String(c.subjectTypeID) === String(id)
      );
      return found ? found.subjectTypeNameTh : String(id);
    },
    getMajorName(id) {
      const found = this.curriculumList.find(
        c => String(c.majorID) === String(id)
      );
      return found ? found.majorNameTh : String(id);
    },

    // ── Load colleges ──
    async loadColleges() {
      try {
        const result = await this.$http.post("college.php", {
          ApiKey: this.ApiKey
        });
        this.colleges = result.data || [];
      } catch (e) {
        console.error(e);
      }
    },

    // ── Load curriculum (ประเภทวิชา / สาขาวิชา) ──
    async loadCurriculum() {
      this.loadingCurriculum = true;
      try {
        const result = await this.$http.post("curriculum.php", {
          ApiKey: this.ApiKey
        });
        this.curriculumList = result.data || [];
      } catch (e) {
        console.error("curriculum load error:", e);
      } finally {
        this.loadingCurriculum = false;
      }
    },

    // ── เมื่อเปลี่ยนปี: โหลดใหม่ + ล้าง filter ──
    async onYearChange() {
      this.collegeSearch = "";
      this.clearAdvancedFilters();
      await this.searchYearSubmit();
    },

    // ── เมื่อเปลี่ยนประเภทวิชา: reset สาขาวิชาที่เลือก แล้ว filter ──
    onSubjectTypeChange() {
      // ล้างสาขาวิชาที่ไม่ตรงกับประเภทวิชาใหม่
      const validMajorIDs = new Set(this.majorOptions.map(m => m.majorID));
      this.filterMajors = this.filterMajors.filter(id => validMajorIDs.has(id));
      this.applyFilters();
    },

    // ── applyFilters: ไม่ต้องเรียก API ซ้ำ เพราะ filteredData เป็น computed ──
    applyFilters() {
      // trigger re-fetch ถ้า collegeSearch หรือ year เปลี่ยน
      if (this.collegeSearch) {
        this.fetchData(this.searchYear, this.collegeSearch);
      } else {
        this.fetchData(this.searchYear, null);
      }
    },

    async searchYearSubmit() {
      if (!this.searchYear) return;
      this.loading = true;
      this.collegeSearch = "";
      await this.fetchData(this.searchYear, null);
      this.loading = false;
    },

    async fetchData(year, collegeCode) {
      this.loading = true;
      try {
        const payload = { ApiKey: this.ApiKey, rate_work_college_year: year };
        if (collegeCode) payload.rate_work_college_code = collegeCode;

        const [result, resultReport] = await Promise.all([
          this.$http.post("rate_work_college_admin.php", {
            ...payload,
            action: "list"
          }),
          this.$http.post("rate_work_college_admin.php", {
            ...payload,
            action: "report"
          })
        ]);
        this.rate_work_colleges = result.data || [];
        this.rate_work_collegesReport = resultReport.data || [];
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาดในการโหลดข้อมูล"
        );
      } finally {
        this.loading = false;
      }
    },

    // ── ล้าง filter ขั้นสูง ──
    clearAdvancedFilters() {
      this.filterLevels = [];
      this.filterSubjectTypes = [];
      this.filterMajors = [];
    },

    // ── ลบ filter ทีละตัว (จาก chip) ──
    removeFilterLevel(val) {
      this.filterLevels = this.filterLevels.filter(v => v !== val);
    },
    removeFilterSubjectType(val) {
      this.filterSubjectTypes = this.filterSubjectTypes.filter(v => v !== val);
      this.onSubjectTypeChange();
    },
    removeFilterMajor(val) {
      this.filterMajors = this.filterMajors.filter(v => v !== val);
    },

    // ── CSV ──
    rate_work_collegeCSV() {
      this.selectedFile = null;
      this.csvrate_work_collegedialog = true;
    },
    async csvrate_work_collegeSubmit() {
      if (!this.selectedFile) {
        this.showSnackbar("warning", "mdi-alert", "กรุณาเลือกไฟล์");
        return;
      }
      await this.sweetAlertLoading();
      try {
        const formData = new FormData();
        const filename = `${Date.now()}.rate_work_college.csv`;
        formData.append("file", this.selectedFile);
        formData.append("filename", `../HRvecfiles/${filename}`);
        formData.append("ApiKey", this.ApiKey);
        const result = await this.$http.post(
          "rate_work_collegecsv.php",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        Swal.close();
        if (result.data.status === true) {
          this.showSnackbar(
            "success",
            "mdi-check-circle",
            "นำเข้าข้อมูลสำเร็จ"
          );
          await this.searchYearSubmit();
        } else {
          this.showSnackbar(
            "error",
            "mdi-alert-circle",
            "เกิดข้อผิดพลาดในการนำเข้าข้อมูล"
          );
        }
      } catch (e) {
        Swal.close();
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาด");
      } finally {
        this.csvrate_work_collegedialog = false;
      }
    },

    // ── API ศธ.02 ──
    std_getstudentcountQuery() {
      this.AcademicYearst = this.getCurrentYear();
      this.Semesterst = "";
      this.stdrate_work_collegedialog = true;
    },
    async stdrate_work_collegeSubmit() {
      if (!this.AcademicYearst || !this.Semesterst) {
        this.showSnackbar("warning", "mdi-alert", "กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }
      await this.sweetAlertLoading();
      try {
        this.loading = true;
        await this.$http.post("GetStudentCount.php", {
          ApiKey: this.ApiKey,
          AcademicYear: this.AcademicYearst,
          Semester: this.Semesterst
        });
        Swal.close();
        this.showSnackbar(
          "success",
          "mdi-check-circle",
          "ดึงข้อมูลจาก API สำเร็จ"
        );
        await this.searchYearSubmit();
      } catch (e) {
        Swal.close();
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาดในการดึงข้อมูล"
        );
      } finally {
        this.loading = false;
        this.stdrate_work_collegedialog = false;
      }
    },

    // ── Export Excel (ใช้ filteredData เสมอ) ──
    exportToExcel() {
      const {
        ch1,
        ch2,
        ch3,
        s1,
        s2,
        totalVC,
        totalHVC,
        total
      } = this.statistics;

      const exportData = this.filteredData.map(item => {
        const lv1 = parseInt(item.rate_work_field_study_lavel_1) || 0;
        const lv2 = parseInt(item.rate_work_field_study_lavel_2) || 0;
        const lv3 = parseInt(item.rate_work_field_study_lavel_3) || 0;
        const isVC = String(item.rate_work_college_id_class) === "20";
        return {
          ปีการศึกษา: item.rate_work_college_year,
          วิทยาลัย: item.college_name,
          ประเภทวิชา: item.subject_typeName,
          ระดับชั้น: isVC ? "ปวช." : "ปวส.",
          สาขาวิชา: item.name_type_course,
          "ชั้นปีที่ 1": lv1,
          "ชั้นปีที่ 2": lv2,
          "ชั้นปีที่ 3": isVC ? lv3 : 0,
          รวม: isVC ? lv1 + lv2 + lv3 : lv1 + lv2
        };
      });

      exportData.push({});
      exportData.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวช.",
        "ชั้นปีที่ 1": ch1,
        "ชั้นปีที่ 2": ch2,
        "ชั้นปีที่ 3": ch3,
        รวม: totalVC
      });
      exportData.push({
        ปีการศึกษา: "สรุป",
        ประเภทวิชา: "ปวส.",
        "ชั้นปีที่ 1": s1,
        "ชั้นปีที่ 2": s2,
        "ชั้นปีที่ 3": 0,
        รวม: totalHVC
      });
      exportData.push({ ปีการศึกษา: "รวมทั้งหมด", รวม: total });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ข้อมูลนักเรียน");
      ws["!cols"] = [
        { wch: 12 },
        { wch: 35 },
        { wch: 18 },
        { wch: 8 },
        { wch: 40 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 }
      ];
      XLSX.writeFile(wb, `ข้อมูลนักเรียน_${this.searchYear}.xlsx`);
      this.showSnackbar(
        "success",
        "mdi-check-circle",
        "ส่งออกไฟล์ Excel สำเร็จ"
      );
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&display=swap");

.student-data-report {
  font-family: "Sarabun", sans-serif;
  background: linear-gradient(135deg, #f5f5f5 0%, #fbecec 100%);
  min-height: 100vh;
}

/* Header */
.header-section {
  background: var(--hv-hero);
  border-radius: 16px;
  padding: 24px 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(25, 118, 210, 0.3);
}
.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}
.page-subtitle {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}
.header-badges {
  gap: 8px;
}
.header-chip {
  font-weight: 500;
}

/* Stat Cards */
.stat-card {
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}
.stat-card-blue {
  border-left-color: #455a64;
}
.stat-card-purple {
  border-left-color: #b3151b;
}
.stat-card-teal {
  border-left-color: #263238;
}
.stat-card-orange {
  border-left-color: #A86400;
}
.stat-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
}
.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}
.stat-percent {
  font-size: 0.8rem;
  color: #999;
  margin: 0;
}
.stat-icon {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Charts */
.chart-card {
  border-radius: 16px;
  overflow: hidden;
}
.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}

/* Control Panel */
.control-panel {
  border-radius: 16px;
  position: sticky;
  top: 20px;
}
.control-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
}
.action-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.action-btn {
  text-transform: none;
  font-weight: 500;
}

/* Advanced Filter */
.advanced-filter-header {
  padding: 6px 8px;
  border-radius: 8px;
  background: #f5f5f5;
  transition: background 0.2s;
}
.advanced-filter-header:hover {
  background: #eeeeee;
}

.filter-section {
}
.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.multi-select-field >>> .v-input__slot {
  min-height: 40px !important;
}

/* Summary */
.summary-card {
  background: #f8f9fa;
  border-radius: 12px;
}
.summary-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
}
.summary-table td {
  padding: 6px 8px !important;
  font-size: 0.875rem;
}
.summary-divider-row td {
  border-top: 1px solid #e0e0e0 !important;
  padding-top: 8px !important;
}
.summary-total-row td {
  border-top: 2px solid #455a64 !important;
  padding-top: 8px !important;
}

/* Data Table */
.data-table-card {
  border-radius: 16px;
  overflow: hidden;
}
.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  padding: 16px 24px;
}
.table-actions {
  gap: 8px;
}
.search-field {
  max-width: 400px;
}

.modern-table >>> th {
  background: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  color: #333 !important;
  white-space: nowrap;
}
.modern-table >>> td {
  font-size: 0.875rem;
  padding: 12px 16px !important;
}
.modern-table >>> tr:hover {
  background: #f5f5f5 !important;
}

.college-name-cell {
  line-height: 1.4;
}
.subject-type {
  font-size: 0.8rem;
  color: #666;
}
.course-name {
  font-size: 0.85rem;
  cursor: pointer;
}
.number-cell {
  font-family: "Roboto Mono", monospace;
  font-weight: 500;
}

/* Dialog */
.import-dialog {
  border-radius: 16px;
  overflow: hidden;
}

/* Responsive */
@media (max-width: 960px) {
  .header-section {
    padding: 16px 20px;
  }
  .page-title {
    font-size: 1.25rem;
  }
  .stat-value {
    font-size: 1.5rem;
  }
  .table-actions {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }
  .control-panel {
    position: relative;
    top: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.stat-card,
.chart-card,
.control-panel,
.data-table-card {
  animation: fadeIn 0.5s ease forwards;
}
.stat-card:nth-child(1) {
  animation-delay: 0.1s;
}
.stat-card:nth-child(2) {
  animation-delay: 0.2s;
}
.stat-card:nth-child(3) {
  animation-delay: 0.3s;
}
.stat-card:nth-child(4) {
  animation-delay: 0.4s;
}
</style>
